import Container from '@brands/Components/Container/Container';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PWDRequisite from '@brands/Components/PasswordValidation/PasswordValidation';
import { useWindowSize } from '@brands/hooks';
import { changePassword } from '@brands/services/auth/changePassword';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { toastError } from '@brands/Utils/toastError';
import { hasCapsLetter, hasNumber, hasSpecialChar } from '@brands/Utils/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import zIndex from '@mui/material/styles/zIndex';
import React, { BaseSyntheticEvent, FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineClose } from 'react-icons/ai';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import styles from './styles.module.scss';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

interface ResetPasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const ChangePasswordModal: FC<ChangePasswordModalProps> = ({ isOpen, onClose, email }) => {
  const { width } = useWindowSize();
  const viewportWidth = width || (typeof window !== 'undefined' ? window.innerWidth : 0);
  const isMobile = viewportWidth < 768;

  const [isDisabled, setIsDisabled] = useState(true);
  const [checks, setChecks] = useState({
    capsLetterCheck: false,
    numberCheck: false,
    pwdLengthCheck: false,
    specialFlag: false,
  });
  const [loadingState, setLoadingState] = React.useState(false);

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Current Password is required'),
    newPassword: Yup.string()
      .required('New Password is required')
      .min(8)
      .matches(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[><?@_+'`~^%&\*\[\]\{\}.!#|\\\"$';,:;=/\(\),\-])(?=.{8,})/),
    confirmNewPassword: Yup.string()
      .required('Confirm New Password is required')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
    setError,
  } = useForm<ResetPasswordForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onReset = async (data: ResetPasswordForm): Promise<void> => {
    if (data.newPassword !== data.confirmNewPassword) {
      toastError('New passwords do not match.');
      return;
    }

    try {
      setLoadingState(true);
      await changePassword({ email, old_password: data.currentPassword, new_password: data.newPassword });
      toast.success('Password changed successfully');
      onClose();
    } catch (error) {
      displayErrorDetails(error);
      setLoadingState(false);
    }
  };

  const handleOnChange = (e: BaseSyntheticEvent, name: string): void => {
    const { value } = e.target;
    setValue(name as 'currentPassword' | 'newPassword' | 'confirmNewPassword', value);

    // The inputs' own onChange replaces the one register() supplies, so the
    // schema's match rule never runs while typing. Without this the save button
    // just stays disabled with nothing saying why.
    const newPassword = getValues('newPassword');
    const confirmNewPassword = getValues('confirmNewPassword');
    if (newPassword && confirmNewPassword && newPassword !== confirmNewPassword) {
      setError('confirmNewPassword', { type: 'manual', message: "Passwords don't match" });
    } else {
      clearErrors('confirmNewPassword');
    }
    if (name === 'newPassword') {
      const capsLetterCheck = hasCapsLetter(value);
      const numberCheck = hasNumber(value);
      const specialFlag = hasSpecialChar(value);
      const pwdLengthCheck = value.length >= 8;
      setChecks({
        capsLetterCheck,
        numberCheck,
        pwdLengthCheck,
        specialFlag,
      });
    }
    if (
      errors.confirmNewPassword !== undefined ||
      errors.newPassword !== undefined ||
      errors.currentPassword !== undefined ||
      getValues('confirmNewPassword') === '' ||
      getValues('newPassword') === '' ||
      getValues('currentPassword') === '' ||
      getValues('confirmNewPassword') !== getValues('newPassword')
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  };

  if (isMobile) {
    return (
      <Container
        patientDashboard
        isSidenavVisible
        childClassName={styles.changePasswordDashboard}
        containerStyle={{
          justifyContent: 'start',
          overflowY: 'scroll',
          zIndex: zIndex.drawer + 1,
        }}
      >
        <div className={styles.changePasswordDashboardContainer}>
          <div className={styles.changePasswordCard}>
            <button type="button" className={styles.backButton} onClick={onClose} aria-label="Back" disabled={loadingState}>
              <MdKeyboardArrowLeft />
              Back
            </button>
            {loadingState ? (
              <Loading dotted isHeightAuto />
            ) : (
              <div className={styles.changePasswordContainer}>
                <p className={`fs-unmask ${styles.title}`}>Change password</p>
                <form className={styles.formContainer} onSubmit={handleSubmit(onReset)}>
                  <div className={styles.inputWrap}>
                    <div className={styles.inputWrapper}>
                      <TextField
                        {...register('currentPassword')}
                        label="Current password"
                        type="password"
                        errors={
                          errors.currentPassword && errors.currentPassword.message !== 'Check your password'
                            ? errors
                            : {}
                        }
                        name="currentPassword"
                        showPasswordToggle
                        onChange={(e) => {
                          handleOnChange(e, 'currentPassword');
                        }}
                        autoComplete="current-password"
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                      />
                    </div>
                  </div>
                  <div className={styles.inputWrap}>
                    <div className={styles.inputWrapper}>
                      <TextField
                        {...register('newPassword')}
                        label="New password"
                        type="password"
                        name="newPassword"
                        showPasswordToggle
                        onChange={(e) => {
                          handleOnChange(e, 'newPassword');
                        }}
                        errors={errors.newPassword ? errors : {}}
                        autoComplete="new-password"
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <PWDRequisite
                      capsLetterFlag={checks.capsLetterCheck ? 'valid' : 'invalid'}
                      numberFlag={checks.numberCheck ? 'valid' : 'invalid'}
                      pwdLengthFlag={checks.pwdLengthCheck ? 'valid' : 'invalid'}
                      specialFlag={checks.specialFlag ? 'valid' : 'invalid'}
                      fontSize="12px"
                    />
                  </div>
                  <div className={styles.inputWrap}>
                    <div className={styles.inputWrapper} data-validate="Enter password">
                      <TextField
                        {...register('confirmNewPassword')}
                        errors={errors.confirmNewPassword ? errors : {}}
                        name="confirmNewPassword"
                        type="password"
                        onChange={(e) => {
                          handleOnChange(e, 'confirmNewPassword');
                          if (getValues('confirmNewPassword') === getValues('newPassword')) {
                            clearErrors('confirmNewPassword');
                          }
                        }}
                        autoComplete="new-password"
                        showPasswordToggle
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                        label="Confirm password"
                      />
                      {errors.confirmNewPassword && (
                        <div className={`${styles.invalidFeedback} invalid-feedback`}>
                          {errors.confirmNewPassword.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.buttonContainer}>
                    <div className={styles.buttonWrapper}>
                      <Button
                        type="submit"
                        variant="contained"
                        className={`fs-unmask ${styles.formBtn}`}
                        disabled={isDisabled}
                      >
                        Save changes
                      </Button>
                    </div>
                    <Button variant="text" onClick={() => onClose()} className={`fs-unmask ${styles.formCancelBtn}`}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </Container>
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        if (!loadingState) onClose();
      }}
      style={customStylesDashboard}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      <div className={styles.changePasswordCard}>
        <button
          type="button"
          className={`fs-unmask ${styles.modalClose}`}
          data-micromodal-close=""
          onClick={onClose}
          aria-label="Close modal"
          disabled={loadingState}
        >
          <AiOutlineClose className={styles.icon} />
        </button>
        <button type="button" className={styles.backButton} onClick={onClose} aria-label="Back" disabled={loadingState}>
          <MdKeyboardArrowLeft />
          Back
        </button>
        {loadingState ? (
          <Loading dotted isHeightAuto />
        ) : (
          <div className={styles.changePasswordContainer}>
            <p className={`fs-unmask ${styles.title}`}>Change password</p>
            <form className={styles.formContainer} onSubmit={handleSubmit(onReset)}>
              <div className={styles.inputWrap}>
                <div className={styles.inputWrapper}>
                  <TextField
                    {...register('currentPassword')}
                    label="Current password"
                    type="password"
                    errors={
                      errors.currentPassword && errors.currentPassword.message !== 'Check your password' ? errors : {}
                    }
                    name="currentPassword"
                    showPasswordToggle
                    onChange={(e) => {
                      handleOnChange(e, 'currentPassword');
                    }}
                    autoComplete="current-password"
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                  />
                </div>
              </div>
              <div className={styles.inputWrap}>
                <div className={styles.inputWrapper}>
                  <TextField
                    {...register('newPassword')}
                    label="New password"
                    type="password"
                    name="newPassword"
                    showPasswordToggle
                    onChange={(e) => {
                      handleOnChange(e, 'newPassword');
                    }}
                    errors={errors.newPassword ? errors : {}}
                    autoComplete="new-password"
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                  />
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <PWDRequisite
                  capsLetterFlag={checks.capsLetterCheck ? 'valid' : 'invalid'}
                  numberFlag={checks.numberCheck ? 'valid' : 'invalid'}
                  pwdLengthFlag={checks.pwdLengthCheck ? 'valid' : 'invalid'}
                  specialFlag={checks.specialFlag ? 'valid' : 'invalid'}
                  fontSize="12px"
                />
              </div>
              <div className={styles.inputWrap}>
                <div className={styles.inputWrapper} data-validate="Enter password">
                  <TextField
                    {...register('confirmNewPassword')}
                    errors={errors.confirmNewPassword ? errors : {}}
                    name="confirmNewPassword"
                    type="password"
                    onChange={(e) => {
                      handleOnChange(e, 'confirmNewPassword');
                      if (getValues('confirmNewPassword') === getValues('newPassword')) {
                        clearErrors('confirmNewPassword');
                      }
                    }}
                    autoComplete="new-password"
                    showPasswordToggle
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                    label="Confirm password"
                  />
                  {errors.confirmNewPassword && (
                    <div className={`${styles.invalidFeedback} invalid-feedback`}>
                      {errors.confirmNewPassword.message}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.buttonContainer}>
                <div className={styles.buttonWrapper}>
                  <Button
                    type="submit"
                    variant="contained"
                    className={`fs-unmask ${styles.formBtn}`}
                    disabled={isDisabled}
                  >
                    Save changes
                  </Button>
                </div>
                <Button variant="text" onClick={() => onClose()} className={`fs-unmask ${styles.formCancelBtn}`}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
